import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';

interface EscapePreviewProps {
  showPlayButton?: boolean;
}

const EscapePreview: React.FC<EscapePreviewProps> = ({ showPlayButton = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;

    const ballColors = ['#00e5ff', '#d500f9', '#ffea00', '#ff0080', '#00ff00', '#ff9100'];
    
    // Simulation State
    let ball = { x: 0, y: 0, vx: 2, vy: 0, radius: 8, color: ballColors[Math.floor(Math.random() * ballColors.length)] };
    let containerRadius = 160; // Base value, will be updated in resize
    
    interface Ring {
      radius: number;
      angle: number; // Current rotation angle
      speed: number;
      gapSize: number; // In radians
      active: boolean;
      color: string;
      width: number;
    }

    const getRings = (radius: number): Ring[] => [
      { radius: radius * 0.75, angle: 0, speed: 0.02, gapSize: 1.2, active: true, color: '#00e5ff', width: 6 },
      { radius: radius * 0.625, angle: 2, speed: -0.015, gapSize: 1.2, active: true, color: '#d500f9', width: 6 },
      { radius: radius * 0.5, angle: 4, speed: 0.025, gapSize: 1.2, active: true, color: '#ffea00', width: 6 },
      { radius: radius * 0.375, angle: 1, speed: -0.03, gapSize: 1.2, active: true, color: '#ff0080', width: 6 },
      { radius: radius * 0.25, angle: 3, speed: 0.035, gapSize: 1.2, active: true, color: '#00ff00', width: 6 },
    ];

    let rings: Ring[] = getRings(containerRadius);

    const reset = () => {
      rings = getRings(containerRadius);
      ball = { x: 0, y: 0, vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8, radius: 8, color: ballColors[Math.floor(Math.random() * ballColors.length)] };
    };

    const resize = () => {
      if (containerRef.current && canvas) {
        const { clientWidth, clientHeight } = containerRef.current;
        width = clientWidth;
        height = clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        centerX = width / 2;
        centerY = height / 2;
        containerRadius = (Math.min(width, height) / 2) * 0.8;
        reset(); // Re-initialize rings with new radius
      }
    };

    window.addEventListener('resize', resize);
    resize();

    const update = () => {
      const gravity = 0.2;
      const friction = 0.999;

      // Ball Physics
      ball.vy += gravity;
      ball.vx *= friction;
      ball.vy *= friction;

      // Store previous position to detect tunnel/crossing
      const prevX = ball.x;
      const prevY = ball.y;
      const prevDist = Math.sqrt(prevX * prevX + prevY * prevY);

      ball.x += ball.vx;
      ball.y += ball.vy;

      const currDist = Math.sqrt(ball.x * ball.x + ball.y * ball.y);

      // Check Rings Collision
      let allBroken = true;

      rings.forEach(ring => {
        if (!ring.active) return;
        allBroken = false;

        ring.angle += ring.speed;

        // Determine if ball crossed the ring boundary this frame
        // It could be moving Outwards (prev < R < curr) or Inwards (prev > R > curr)
        const crossedOut = prevDist < ring.radius - ring.width/2 && currDist >= ring.radius - ring.width/2;
        const crossedIn = prevDist > ring.radius + ring.width/2 && currDist <= ring.radius + ring.width/2;

        if (crossedOut || crossedIn) {
          // Calculate angle of the ball at collision
          let ballAngle = Math.atan2(ball.y, ball.x);
          if (ballAngle < 0) ballAngle += Math.PI * 2;

          // Normalize ring angle
          let ringAngle = ring.angle % (Math.PI * 2);
          if (ringAngle < 0) ringAngle += Math.PI * 2;

          // Check if ball angle is inside the gap
          // Gap is defined from [ringAngle, ringAngle + gapSize]
          // Handle wrap around logic
          const gapStart = ringAngle;
          const gapEnd = ringAngle + ring.gapSize;
          
          let inGap = false;
          
          // Simple wrap around check
          const normalize = (a: number) => (a + Math.PI * 2) % (Math.PI * 2);
          
          // Check if ballAngle is between Start and End (handling wrap)
          // Easiest way: rotate ballAngle backwards by gapStart, check if it's < gapSize
          let relativeAngle = normalize(ballAngle - gapStart);
          
          if (relativeAngle < ring.gapSize) {
            inGap = true;
          }

          if (inGap) {
            ring.active = false;
            // Add a little "pop" velocity or effect here?
          } else {
            // Bounce
            // Normal is radial: (x/dist, y/dist)
            const nx = ball.x / currDist;
            const ny = ball.y / currDist;
            
            // Reflect
            const dot = ball.vx * nx + ball.vy * ny;
            ball.vx = (ball.vx - 2 * dot * nx);
            ball.vy = (ball.vy - 2 * dot * ny);

            // Push out of ring to prevent sticking
            // If crossed OUT, push IN. If crossed IN, push OUT.
            if (crossedOut) {
               const overlap = currDist - (ring.radius - ring.width/2 - ball.radius);
               ball.x -= nx * (overlap + 1);
               ball.y -= ny * (overlap + 1);
            } else {
               const overlap = (ring.radius + ring.width/2 + ball.radius) - currDist;
               ball.x += nx * (overlap + 1);
               ball.y += ny * (overlap + 1);
            }
          }
        }
      });

      if (allBroken && currDist > containerRadius + 50) {
        reset();
      }

      // Outer Container Bounds (Reset if lost)
      if (currDist > containerRadius + 100) {
        reset();
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.translate(centerX, centerY);

      // Draw Container
      ctx.beginPath();
      ctx.arc(0, 0, containerRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-containerRadius, -containerRadius, containerRadius, containerRadius);
      grad.addColorStop(0, '#00e5ff');
      grad.addColorStop(0.5, '#d500f9');
      grad.addColorStop(1, '#ffea00');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw Rings
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      
      rings.forEach(ring => {
      if (!ring.active) {
            // Draw faint ghost of broken ring
            ctx.beginPath();
            ctx.strokeStyle = ring.color;
            ctx.globalAlpha = 0.15;
            ctx.arc(0, 0, ring.radius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.globalAlpha = 1.0;
            return;
        }

        ctx.beginPath();
        ctx.strokeStyle = ring.color;
        // Arc from End of Gap to Start of Gap
        // Gap is [Angle, Angle + GapSize]
        // So Arc is [Angle + GapSize, Angle + 2PI]
        ctx.arc(0, 0, ring.radius, ring.angle + ring.gapSize, ring.angle + Math.PI * 2);
        ctx.stroke();
      });

      // Draw Ball
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
      ctx.fillStyle = ball.color;
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.restore();
    };

    const loop = () => {
      update();
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="group relative rounded-[3rem] overflow-hidden aspect-square bg-[#f8fafc] border border-zinc-200 hover:border-black transition-all duration-500 shadow-2xl">
      <canvas ref={canvasRef} className="block" />
      
      {showPlayButton && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-white/40 backdrop-blur-[2px]">
          <Link 
            to="/escape-velocity/level-1"
            className="px-6 py-3 bg-black text-white font-bold rounded-full flex items-center gap-2 hover:scale-105 transition-transform shadow-xl shadow-black/20"
          >
            <Play className="w-4 h-4 fill-current" />
            Play Level 1
          </Link>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white to-transparent pointer-events-none">
        <h3 className="text-black font-bold text-lg uppercase tracking-tight">PHYSICS</h3>
        <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Timing Challenge</p>
      </div>
    </div>
  );
};

export default EscapePreview;
